package com.patronat.top.library.media.domain.usecase

import com.patronat.top.dto.ImageType
import com.patronat.top.library.media.data.FileSource
import com.patronat.top.library.media.data.KmpFileSource
import com.patronat.top.library.media.utils.ImageCompressor
import io.github.vinceglb.filekit.PlatformFile

internal data class PreparedMediaUpload(
    val file: FileSource,
    val mimeType: String
)

internal object MediaUploadPreprocessor {
    private const val JpegMimeType = "image/jpeg"
    private const val AvatarSizePx = 512
    private const val PhotoMaxSidePx = 1920
    private const val AvatarQuality = 0.82
    private const val PhotoQuality = 0.82

    private val serverSupportedImageMimeTypes = setOf(
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp"
    )

    suspend fun prepare(
        file: FileSource,
        type: ImageType,
        mimeType: String
    ): PreparedMediaUpload? {
        val normalizedMimeType = mimeType.ifBlank { JpegMimeType }.lowercase()
        if (!normalizedMimeType.startsWith("image/")) {
            return PreparedMediaUpload(file = file, mimeType = normalizedMimeType)
        }

        val platformFile = file.platformSource as? PlatformFile
        if (platformFile == null) {
            return if (normalizedMimeType in serverSupportedImageMimeTypes) {
                PreparedMediaUpload(file = file, mimeType = normalizedMimeType)
            } else {
                null
            }
        }

        val compressedBytes = runCatching {
            when (type) {
                ImageType.AVATAR -> ImageCompressor.compressAndCrop(
                    file = platformFile,
                    maxWidth = AvatarSizePx,
                    maxHeight = AvatarSizePx,
                    quality = AvatarQuality
                )
                ImageType.BUSINESS, ImageType.COMMENTS -> ImageCompressor.compressToJpeg(
                    file = platformFile,
                    maxWidth = PhotoMaxSidePx,
                    maxHeight = PhotoMaxSidePx,
                    quality = PhotoQuality
                )
            }
        }.getOrNull()

        if (compressedBytes == null || compressedBytes.isEmpty()) {
            return if (normalizedMimeType in serverSupportedImageMimeTypes) {
                PreparedMediaUpload(file = file, mimeType = normalizedMimeType)
            } else {
                null
            }
        }

        return PreparedMediaUpload(
            file = KmpFileSource(
                name = file.name.withJpegExtension(),
                bytes = compressedBytes,
                platformSource = platformFile
            ),
            mimeType = JpegMimeType
        )
    }

    private fun String.withJpegExtension(): String {
        val baseName = substringBeforeLast('.', missingDelimiterValue = this).ifBlank { "image" }
        return "$baseName.jpg"
    }
}
